/**
 * Stripe Plan Fields Component
 *
 * Building block for payment plan selection and options.
 * Used by purchase triggers (any, some).
 *
 * Note: stripePlan is stored as { id, nickname } object, not just an ID.
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { fetchAllStripePlans } from 'src/models/payment/payment';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { formatCurrency } from 'src/utils/numberFormatter';

import { getAutomationAction } from '../automationActions';
import { AutomationItem } from '../types';

interface StripePlanFieldsProps {
  automation: AutomationItem;
  index: number;
  showPaymentPlan?: boolean;
  showTrialOptions?: boolean;
  hideEveryPayment?: boolean;
}

export const StripePlanFields: FC<StripePlanFieldsProps> = ({
  automation,
  index,
  showPaymentPlan = true,
  showTrialOptions = true,
  hideEveryPayment,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'automations.stripePlan' });
  const { setFieldValue } = useFormikContext();
  const { user } = useSession();

  const actionHideEveryPayment =
    getAutomationAction(automation.customType)?.hideEveryPayment ?? false;
  const shouldHideEveryPayment = hideEveryPayment ?? actionHideEveryPayment;

  const { data: stripePlans = [], isLoading, isRefetching } = useQuery({
    queryKey: ['stripePlans', user?.id],
    queryFn: () => fetchAllStripePlans(),
    staleTime: 60000,
  });

  const formatPlanLabel = (plan: {
    name: string;
    amount: number;
    currency: string;
    interval: string;
    intervalCount: number;
  }) => {
    const amount = formatCurrency(plan.amount / 100.0, {
      currency: plan.currency,
    });
    const interval =
      plan.intervalCount > 1
        ? `${plan.intervalCount} ${plan.interval}s`
        : plan.interval;
    return `${plan.name} (${amount}/${interval})`;
  };

  const stripePlanOptions = [
    { value: '', label: t('anyOrNoPlan') },
    ...stripePlans
      .filter((plan) => plan.active)
      .map((plan) => ({
        value: plan.id,
        label: formatPlanLabel(plan),
      })),
  ];

  // Shadow field name for FormSelect (stores just the ID)
  const fieldName = `automations.${index}._stripePlanId`;
  const [initialized, setInitialized] = useState(false);

  // Initialize the shadow field with current stripePlan.id (wait until data loaded)
  // useLayoutEffect ensures value is set before FormSelect reads it
  useLayoutEffect(() => {
    if (!isLoading && !isRefetching) {
      setFieldValue(fieldName, automation.stripePlan?.id || '');
      setInitialized(true);
    }
  }, [automation.stripePlan?.id, fieldName, setFieldValue, isLoading, isRefetching]);

  const handlePlanChange = (planId: string) => {
    if (!planId) {
      setFieldValue(`automations.${index}.stripePlan`, null);
    } else {
      const selectedPlan = stripePlans.find((p) => p.id === planId);
      setFieldValue(`automations.${index}.stripePlan`, {
        id: planId,
        nickname: selectedPlan?.name || '',
      });
    }
  };

  if (!showPaymentPlan && !showTrialOptions && shouldHideEveryPayment) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {showPaymentPlan && (
        <Box sx={{ maxWidth: 400 }}>
          {isLoading || isRefetching || !initialized ? (
            <Loader size="small" />
          ) : (
            <FormSelect
              name={fieldName}
              label={t('paymentPlan')}
              options={stripePlanOptions}
              prompt={t('selectPlan')}
              onChange={(e) => handlePlanChange(e.target.value as string)}
            />
          )}
        </Box>
      )}
      {showTrialOptions && (
        <Checkbox
          label={t('onlyAfterTrial')}
          checked={Boolean(automation.afterTrial)}
          onChange={(e) =>
            setFieldValue(
              `automations.${index}.afterTrial`,
              e.target.checked ? true : false
            )
          }
        />
      )}
      {!shouldHideEveryPayment && (
        <Checkbox
          label={t('everyPayment')}
          checked={Boolean(automation.everyPayment)}
          onChange={(e) =>
            setFieldValue(
              `automations.${index}.everyPayment`,
              e.target.checked ? true : false
            )
          }
        />
      )}
    </Stack>
  );
};
